import React, { type ReactNode, useCallback } from 'react';
import { styled } from '@compiled/react';
import Select from '@atlaskit/select';
import { gridSize } from '@atlaskit/theme';
import type { IssueLinkType } from '../../types';

interface Props {
	placeholder?: ReactNode;
	value?: IssueLinkType['id'];
	clearable?: boolean;
	disabled?: boolean;
	onChange?: (next: IssueLinkType['id'] | undefined) => void;
	'aria-label'?: string;
	options: IssueLinkType[];
}

export const IssueLinkTypeSelect = ({
	placeholder,
	value,
	clearable = false,
	disabled = false,
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onChange = () => {},
	'aria-label': ariaLabel,
	options,
}: Props) => {
	const transformedOptions = options.map(({ id, name }) => ({
		value: id,
		label: name,
	}));
	const handleSelectChange = useCallback(
		<T extends { value?: string | number }>(selected: T | null) => {
			onChange(selected?.value !== undefined ? parseInt(`${selected.value}`, 10) : undefined);
		},
		[onChange],
	);
	const selected = transformedOptions.find((option) => option.value === value);

	return (
		<Container>
			<Select
				aria-label={ariaLabel}
				isDisabled={transformedOptions.length === 0 || disabled}
				menuPlacement="auto"
				width={176}
				value={selected ?? null}
				onChange={handleSelectChange}
				isClearable={clearable}
				options={transformedOptions}
				placeholder={placeholder}
			/>
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: gridSize() * 24,
});
